import React from 'react';
import { Wallet } from 'lucide-react';

export const AllUsersBalances = ({ balances }) => {
  if (!balances || balances.length === 0) return null;
  return (
    <div className="bg-white rounded-xl shadow-lg border-2 border-indigo-300 p-6" data-testid="all-users-balances">
      <div className="flex items-center gap-2 mb-4">
        <Wallet className="w-6 h-6 text-indigo-600" />
        <h2 className="text-xl font-bold text-gray-800">All Users Cash Balances</h2>
        <span className="ml-auto text-sm text-gray-600">({balances.length} users)</span>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-indigo-50 rounded-lg">
        <div>
          <p className="text-sm text-gray-600">Users with Balance</p>
          <p className="text-2xl font-bold text-green-600">
            {balances.filter(b => b.current_balance >= 0).length}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-600">Users Owing Money</p>
          <p className="text-2xl font-bold text-red-600">
            {balances.filter(b => b.current_balance < 0).length}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-600">Total Outstanding</p>
          <p className="text-2xl font-bold text-orange-600">
            ₹{Math.abs(balances
              .filter(b => b.current_balance < 0)
              .reduce((sum, b) => sum + b.current_balance, 0)
            ).toFixed(2)}
          </p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b-2 border-gray-200">
            <tr>
              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">User</th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Cash Received</th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Expenses</th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Balance</th>
              <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
            </tr>
          </thead>
          <tbody>
            {balances.map((balance) => (
              <tr key={balance.user_email} className="border-b border-gray-100 hover:bg-gray-50 transition">
                <td className="py-3 px-4">
                  <div>
                    <p className="font-semibold text-gray-800">{balance.user_name}</p>
                    <p className="text-xs text-gray-500">{balance.user_email}</p>
                  </div>
                </td>
                <td className="py-3 px-4 text-right">
                  <span className="text-blue-600 font-semibold">₹{balance.total_advances.toFixed(2)}</span>
                </td>
                <td className="py-3 px-4 text-right">
                  <span className="text-orange-600 font-semibold">₹{balance.total_approved_expenses.toFixed(2)}</span>
                </td>
                <td className="py-3 px-4 text-right">
                  <span className={`text-lg font-bold ${
                    balance.current_balance >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {balance.current_balance >= 0 ? '+' : ''}₹{balance.current_balance.toFixed(2)}
                  </span>
                </td>
                <td className="py-3 px-4 text-center">
                  {balance.current_balance < 0 ? (
                    <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">
                      Owes ₹{Math.abs(balance.current_balance).toFixed(2)}
                    </span>
                  ) : balance.current_balance > 0 ? (
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                      Has Balance
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">
                      Settled
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
        <p className="text-sm text-blue-800">
          💡 <strong>Tip:</strong> Users with negative balances have spent more than they received and need reimbursement.
          Users with positive balances still have cash on hand.
        </p>
      </div>
    </div>
  );
};
